import { useState } from "react";
import { Box, Typography, Dialog, IconButton } from "@mui/material";
import { keyframes } from "@mui/system";
import DownloadRounded from "@mui/icons-material/DownloadRounded";
import AudioMessage from "./AudioMessage";

const IMAGE_TYPES = ["png", "jpg", "jpeg", "gif", "webp"];

/* ─────────────── Renkler ─────────────── */
const MY_BG = "#d9fdd3";
const OTHER_BG = "#ffffff";
const MY_BORDER = "#c5eeb8";
const OTHER_BORDER = "#e9edf0";

/* ─────────────── Animasyonlar ─────────────── */
const bubbleInMine = keyframes`
  0%   { opacity: 0; transform: translateX(60px) translateY(8px) scale(0.9); }
  45%  { opacity: 1; transform: translateX(-6px) translateY(0)   scale(1.02); }
  70%  { transform: translateX(3px) scale(0.995); }
  100% { opacity: 1; transform: translateX(0) translateY(0) scale(1); }
`;

const bubbleInOther = keyframes`
  0%   { opacity: 0; transform: translateX(-60px) translateY(8px) scale(0.9); }
  45%  { opacity: 1; transform: translateX(6px)  translateY(0)   scale(1.02); }
  70%  { transform: translateX(-3px) scale(0.995); }
  100% { opacity: 1; transform: translateX(0) translateY(0) scale(1); }
`;

const badgeGlow = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 currentColor; }
  50%      { box-shadow: 0 0 0 3px transparent; }
`;

export default function MessageCard({
  msg,
  name,
  time,
  isMine,
  msgType,
  animate = true,
  index = 0,
}) {
  const enterAnim = isMine ? bubbleInMine : bubbleInOther;
  const delay = animate ? Math.min(index * 0.04, 0.5) : 0;

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: isMine ? "flex-end" : "flex-start",
        mb: 1.25,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          maxWidth: { xs: "82%", sm: "68%" },
          backgroundColor: isMine ? MY_BG : OTHER_BG,
          border: `1px solid ${isMine ? MY_BORDER : OTHER_BORDER}`,
          borderRadius: 2.5,
          borderTopRightRadius: isMine ? 4 : 16,
          borderTopLeftRadius: isMine ? 16 : 4,
          px: 1.25,
          py: 0.9,
          boxShadow: "0 1px 2px rgba(15,50,65,0.06)",
          animation: animate
            ? `${enterAnim} 0.6s cubic-bezier(0.34, 1.4, 0.64, 1) ${delay}s both`
            : "none",
          transition: "box-shadow 0.25s ease, transform 0.25s ease",
          "&:hover": {
            boxShadow: "0 4px 14px rgba(15,50,65,0.10)",
            transform: "translateY(-1px)",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1.5,
            mb: 0.4,
          }}
        >
          <Box
            sx={{
              display: "flex",
              gap: 0.75,
              alignItems: "center",
              minWidth: 0,
            }}
          >
            <Box
              sx={{
                backgroundColor: isMine ? "#2e7d32" : "#6a1b9a",
                color: "#fff",
                px: 0.7,
                py: 0.3,
                borderRadius: 0.6,
                fontSize: 9.5,
                fontWeight: 800,
                letterSpacing: 0.3,
                flexShrink: 0,
                textTransform: "uppercase",
                animation: `${badgeGlow} 2.4s ease-in-out infinite`,
              }}
            >
              {isMine ? "Ben" : "Yetkili"}
            </Box>
            <Typography
              sx={{ fontWeight: 700, fontSize: 12.5, color: "#3b4a54" }}
              noWrap
            >
              {name}
            </Typography>
          </Box>
          <Typography
            sx={{
              fontSize: 10.5,
              color: "#667781",
              flexShrink: 0,
              fontWeight: 600,
              fontVariantNumeric: "tabular-nums", // rakamlar aynı genişlikte
              letterSpacing: "0.02em",
            }}
          >
            {time}
          </Typography>
        </Box>

        <Content msg={msg} msgType={msgType} isMine={isMine} />
      </Box>
    </Box>
  );
}

function Content({ msg, msgType, isMine }) {
  if (IMAGE_TYPES.includes(msgType))
    return <ImageMessage src={msg} isMine={isMine} />;
  if (msgType === "ses") return <AudioMessage src={msg} />;
  if (msgType === "txt" || !msgType) {
    return (
      <Typography
        sx={{
          fontSize: 14,
          lineHeight: 1.45,
          color: "#111b21",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}
      >
        {msg}
      </Typography>
    );
  }
  return (
    <Box
      component="a"
      href={msg}
      target="_blank"
      rel="noreferrer"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        color: "#045781",
        textDecoration: "underline",
        fontSize: 14,
        wordBreak: "break-all",
        transition: "transform 0.2s ease, color 0.2s ease",
        "&:hover": { transform: "translateX(2px)", color: "#033d5c" },
      }}
    >
      .{msgType} {msgType === "pdf" ? "Aç" : "indir"}
    </Box>
  );
}

/* ─────────────── Resim + İndir Butonu ─────────────── */
function ImageMessage({ src }) {
  const [open, setOpen] = useState(false);

  const handleDownload = async (e) => {
    e.stopPropagation();
    try {
      // Dosyayı blob olarak çek → download attribütüyle indir
      const res = await fetch(src, { mode: "cors" });
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = blobUrl;
      const rawName = (src.split("/").pop() || "image").split("?")[0];
      a.download = rawName.includes(".") ? rawName : `${rawName}.jpg`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1500);
    } catch (err) {
      console.warn("Blob indirme başarısız, yeni sekmede açılıyor:", err);
      // CORS hatası olursa yeni sekmede aç (kullanıcı sağ tık → kaydet yapabilir)
      window.open(src, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <>
      <Box
        className="image-wrapper"
        sx={{
          position: "relative",
          display: "inline-block",
          maxWidth: "100%",
        }}
      >
        <Box
          component="img"
          src={src}
          onClick={() => setOpen(true)}
          sx={{
            maxWidth: 260,
            maxHeight: 260,
            borderRadius: 1.5,
            cursor: "pointer",
            objectFit: "contain",
            display: "block",
            transition:
              "transform 0.3s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.3s ease",
            "&:hover": {
              transform: "scale(1.02)",
              boxShadow: "0 6px 18px rgba(15,50,65,0.18)",
            },
            "&:active": { transform: "scale(0.99)" },
          }}
        />

        {/* İndir butonu — mobilde her zaman, desktop'ta hover'da */}
        <IconButton
          size="small"
          onClick={handleDownload}
          title="Resmi indir"
          className="img-download"
          sx={{
            position: "absolute",
            top: 6,
            right: 6,
            width: 32,
            height: 32,
            backgroundColor: "rgba(0,0,0,0.55)",
            color: "#fff",
            backdropFilter: "blur(4px)",
            opacity: { xs: 1, sm: 0 },
            transition:
              "opacity 0.2s ease, background-color 0.2s ease, transform 0.2s ease",
            ".image-wrapper:hover &": { opacity: 1 },
            "&:hover": {
              backgroundColor: "rgba(0,0,0,0.78)",
              transform: "scale(1.08)",
            },
            "&:active": { transform: "scale(0.92)" },
          }}
        >
          <DownloadRounded sx={{ fontSize: 17 }} />
        </IconButton>
      </Box>

      {/* Tam ekran önizleme */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="lg"
        PaperProps={{
          sx: {
            backgroundColor: "transparent",
            boxShadow: "none",
            overflow: "visible",
            position: "relative",
          },
        }}
        BackdropProps={{
          sx: {
            backgroundColor: "rgba(0,0,0,0.85)",
            backdropFilter: "blur(6px)",
          },
        }}
      >
        <Box sx={{ position: "relative", display: "inline-block" }}>
          <Box
            component="img"
            src={src}
            sx={{
              maxWidth: "90vw",
              maxHeight: "90vh",
              display: "block",
              borderRadius: 2,
              animation: "imgZoomIn 0.3s cubic-bezier(0.22, 1, 0.36, 1) both",
              "@keyframes imgZoomIn": {
                from: { opacity: 0, transform: "scale(0.9)" },
                to: { opacity: 1, transform: "scale(1)" },
              },
            }}
          />
          {/* Büyük görünümde de indirme butonu */}
          <IconButton
            size="medium"
            onClick={handleDownload}
            title="Resmi indir"
            sx={{
              position: "absolute",
              top: 12,
              right: 12,
              backgroundColor: "rgba(0,0,0,0.6)",
              color: "#fff",
              backdropFilter: "blur(4px)",
              transition: "background-color 0.2s ease, transform 0.2s ease",
              "&:hover": {
                backgroundColor: "rgba(0,0,0,0.85)",
                transform: "scale(1.08)",
              },
              "&:active": { transform: "scale(0.92)" },
            }}
          >
            <DownloadRounded />
          </IconButton>
        </Box>
      </Dialog>
    </>
  );
}
